/**
 * Kanban board for the project pipeline (v2.7).
 *
 * Seven columns (one per sales stage). Project cards are draggable
 * between columns via @dnd-kit. Optimistic update on drop — API
 * call fires in background, reverts on failure with a toast error.
 *
 * Mobile (<768px): columns render as expanded accordions, no drag.
 * Stage changes on mobile happen via the project overview dropdown.
 */

import { useEffect, useRef, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  closestCenter,
} from "@dnd-kit/core";
import { useDraggable } from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { SALES_STAGES } from "../constants/salesStages.js";
import { useStore } from "../store/useStore.js";
import * as projectsApi from "../services/projects.js";

function hostnameOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function KanbanCard({ project, isDragOverlay = false }) {
  const { id, name, companyUrl, updatedAt } = project;

  const content = (
    <div className={`kanban-card${isDragOverlay ? " kanban-card--dragging" : ""}`}>
      <div className="kanban-card__name">{name}</div>
      {companyUrl && (
        <div className="kanban-card__url">{hostnameOf(companyUrl)}</div>
      )}
      {updatedAt && (
        <div className="kanban-card__meta">
          {new Date(updatedAt).toLocaleDateString()}
        </div>
      )}
    </div>
  );

  if (isDragOverlay) return content;

  return (
    <Link to={`/projects/${id}`} className="kanban-card-link">
      {content}
    </Link>
  );
}

function DraggableCard({ project }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: project.id,
    data: { stage: project.salesStage ?? 0 },
  });
  const wasDragging = useRef(false);
  useEffect(() => {
    if (isDragging) wasDragging.current = true;
  }, [isDragging]);

  const handleClick = (e) => {
    if (wasDragging.current) {
      e.preventDefault();
      wasDragging.current = false;
    }
  };

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={handleClick}
      style={{ opacity: isDragging ? 0.4 : 1 }}
    >
      <KanbanCard project={project} />
    </div>
  );
}

function KanbanColumn({ stage, projects }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `stage-${stage.value}`,
    data: { stage: stage.value },
  });

  return (
    <div
      ref={setNodeRef}
      className={`kanban-column${isOver ? " kanban-column--over" : ""}`}
    >
      <div className="kanban-column__header">
        <div className="kanban-column__name">{stage.name}</div>
        <div className="kanban-column__subtitle">{stage.subtitle}</div>
        <div className="kanban-column__count">{projects.length}</div>
      </div>
      <div className="kanban-column__cards">
        {projects.map((p) => (
          <DraggableCard key={p.id} project={p} />
        ))}
      </div>
    </div>
  );
}

/** Mobile accordion view — default open when stage has projects. */
function MobileStageSection({ stage, projects }) {
  const [open, setOpen] = useState(projects.length > 0);

  return (
    <div className="kanban-mobile-section">
      <button
        type="button"
        className="kanban-mobile-header"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="kanban-mobile-header__name">{stage.name}</span>
        <span className="kanban-mobile-header__subtitle">{stage.subtitle}</span>
        <span className="kanban-mobile-header__count">{projects.length}</span>
        <span className="kanban-mobile-header__chevron">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="kanban-mobile-cards">
          {projects.map((p) => (
              <Link key={p.id} to={`/projects/${p.id}`} className="kanban-card-link">
                <KanbanCard project={p} />
              </Link>
            ))}
        </div>
      )}
    </div>
  );
}

export default function PipelineBoard({ projects }) {
  const updateProjectStage = useStore((s) => s.updateProjectStage);
  const pushToast = useStore((s) => s.pushToast);
  const [activeProject, setActiveProject] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const projectsByStage = useMemo(
    () =>
      SALES_STAGES.map((stage) => ({
        stage,
        projects: projects
          .filter((p) => Number(p.salesStage ?? 0) === stage.value)
          .sort((a, b) => new Date(b.updatedAt ?? 0) - new Date(a.updatedAt ?? 0)),
      })),
    [projects],
  );

  const handleDragStart = (event) => {
    const p = projects.find((pr) => pr.id === event.active.id);
    setActiveProject(p || null);
  };

  const handleDragEnd = async (event) => {
    setActiveProject(null);
    const { active, over } = event;
    if (!over) return;

    const newStage = over.data.current?.stage;
    const oldStage = active.data.current?.stage;
    if (newStage === undefined || newStage === oldStage) return;

    const projectId = active.id;

    // Optimistic update
    updateProjectStage(projectId, newStage);

    try {
      await projectsApi.updateProject(projectId, { salesStage: newStage });
    } catch {
      // Revert on failure
      updateProjectStage(projectId, oldStage);
      pushToast("Failed to update stage", "error");
    }
  };

  return (
    <>
      {/* Desktop: horizontal kanban with drag-and-drop */}
      <div className="kanban-board">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          {projectsByStage.map(({ stage, projects: stageProjects }) => (
            <KanbanColumn key={stage.value} stage={stage} projects={stageProjects} />
          ))}
          <DragOverlay>
            {activeProject ? (
              <KanbanCard project={activeProject} isDragOverlay />
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      {/* Mobile: collapsible accordion sections */}
      <div className="kanban-mobile">
        {projectsByStage.map(({ stage, projects: stageProjects }) => (
          <MobileStageSection
            key={stage.value}
            stage={stage}
            projects={stageProjects}
          />
        ))}
      </div>
    </>
  );
}
